import './index.css';
import { Flex, Text, IconCertifiedSolid } from '@smarterservices/smarter-ui';
import React, { useEffect, useState } from 'react';
import { routeStudent } from './api';
import CountdownTimer from '../CountdownTimer';
import { endSessionProctor } from '../../helpers/helpers';
import Tetris from 'react-tetris';
import DualPane from '../DualPane';
import SinglePane from '../SinglePane';

const WaitingForProctor = ({ stepData, setIsTaskComplete }) => {
  const [routed, setRouted] = useState(false);
  const [showTimer, setShowTimer] = useState(false);

  useEffect(() => {
    const attemptRouting = async () => {
      try {
        const result = await routeStudent();
        if (!result.success && result.error) {
          // There is an error (most likely that there are no proctors available, and could not be rerouted elsewhere)
          // However, they would be a student that is not allowed to fallback to automated, so the session needs to be
          // ended in a way that the student understands why they were forced to quit. (create a popup)
          await endSessionProctor(
            'Your session was forced to end',
            result.error
          );
          setRouted(false);
        } else if (!result.success && result.fallbackToAutomated) {
          // The student does not have any available proctors available, but the institution allows them to continue
          // with automated proctoring.
          // TODO: return in the setStep that you are in automated, otherwise this will advance to ConnectedToProctor
          console.log('Session needs to fallback to automated');
          setRouted(true);
          setIsTaskComplete(true);
        } else if (!result.success) {
          // The student was not able to be routed to a proctor as they were not available, they cannot fall to
          // automated, so we will start counting down the 10 minutes until their session will be ended
          setRouted(false);
          // See if the student refreshed, as the routing timer would be set in session storage
          const storedTime = sessionStorage.getItem('spRoutingTimer');

          if (!storedTime) {
            // Store the now time in session storage so the timer does not reset on a refresh
            sessionStorage.setItem(
              'spRoutingTimer',
              JSON.stringify(Date.now())
            );
          }
          // Show the Timer
          setShowTimer(true);
          setTimeout(() => {
            attemptRouting();
          }, 10000);
        } else if (result.success) {
          // The student was routed to a VTC room!
          setRouted(true);
          setShowTimer(false);
          sessionStorage.removeItem('spRoutingTimer');
        }
      } catch (error) {
        console.log('Error while routing!', { error });
        throw error;
      }
    };

    attemptRouting();
  }, []);

  const renderRoutingContent = () => (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      padding="medium"
    >
      {!routed && !showTimer && <Text>{stepData.attemptingToRoute}</Text>}
      {routed && (
        <Flex direction="column" alignItems="center" gap="small">
          <Flex.Item>
            <IconCertifiedSolid size="large" color="success" />
          </Flex.Item>
          <Flex.Item textAlign="center">
            <Text>{stepData.successfulRoute}</Text>
          </Flex.Item>
        </Flex>
      )}
      {!routed && showTimer && (
        <Flex direction="column" alignItems="center">
          <Text>{stepData.unsuccessfulRoute}</Text>
          <CountdownTimer
            key="spRoutingTimer"
            duration={600}
            label={stepData.timerLabel}
            onExpire={endSessionProctor}
          />
        </Flex>
      )}
    </Flex>
  );

  if (!stepData.showGame) {
    return (
      <SinglePane>
        {renderRoutingContent()}
      </SinglePane>
    );
  }

  return (
    <DualPane
      leftPane={renderRoutingContent()}
      rightPane={
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <div className="tetris-container">
            <Tetris
              keyboardControls={{
                down: 'MOVE_DOWN',
                left: 'MOVE_LEFT',
                right: 'MOVE_RIGHT',
                space: 'HARD_DROP',
                up: 'FLIP_CLOCKWISE',
                p: 'TOGGLE_PAUSE',
              }}
            >
              {({
                Gameboard,
                PieceQueue,
                points,
                linesCleared,
                state,
                controller,
              }) => (
                <div className="tetris-container-inner">
                  <div className="tetris-layout">
                    <div className="tetris-board">
                      <Text className="game-titles">Game Board</Text>
                      <Gameboard />
                    </div>
                    <div className="tetris-queue">
                      <Text className="game-titles">Upcoming Pieces</Text>
                      <PieceQueue />
                    </div>
                  </div>
                  <div className="tetris-info">
                    <div className="tetris-scoreboard">
                      <p>Points: {points}</p>
                      <p>Lines Cleared: {linesCleared}</p>
                    </div>

                    <div className="controls-legend">
                      <h3>Controls</h3>
                      <ul>
                        <li>
                          <strong>Up:</strong> Rotate piece
                        </li>
                        <li>
                          <strong>Left / Right:</strong> Move piece left/right
                        </li>
                        <li>
                          <strong>Down:</strong> Soft drop
                        </li>
                        <li>
                          <strong>Space:</strong> Hard drop
                        </li>
                        <li>
                          <strong>P:</strong> Pause game
                        </li>
                      </ul>
                    </div>

                    {state === 'LOST' && (
                      <div className="game-over-message">
                        <h2>Game Over</h2>
                        <button onClick={controller.restart}>New game</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Tetris>
          </div>
        </Flex>
      }
    />
  );
};

export default WaitingForProctor;
